import {
  available,
  BPS_DENOMINATOR,
  ceilDivide,
  floorFee,
  isPositiveU64,
  isRate,
  isU64,
  U64_MAX,
  unavailable,
} from './integer.js';
import { solveMinimumInputForOutput } from './minimum-input.js';
import { reserveRatioSpotPriceD9 } from './spot-price.js';
import type { PoolMathResult, PoolMathUnavailableReason, SellQuoteOutput } from './types.js';

export interface BondingCurveState {
  /** Immutable virtual-only quote reserve from the BondingCurve account. */
  virtualQuoteReserveD6: bigint;
  /** Current virtual-inclusive quote reserve (`virtual + real`). */
  poolQuoteReserveD6: bigint;
  /** Immutable supply used to define `k = virtual quote * initial supply`. */
  initialOutcomeSupplyD6: bigint;
  /** Current outcome-token balance tracked in the bonding-curve vault. */
  poolOutcomeReserveD6: bigint;
}

export interface BondingCurveFeeConfig {
  /** Original backer split to omit after base-fee rounding; zero for backed markets. */
  backerFeeOmissionBps?: bigint;
  buyFeeBps: bigint;
  sellBaseFeeBps: bigint;
  sellPenaltyFeeBps: bigint;
  prizePoolFeeBps: bigint;
  minimumQuoteTradeD6: bigint;
}

export interface BondingCurveBuyQuote {
  venue: 'bonding_curve';
  direction: 'buy';
  quoteToCurveD6: bigint;
  outcomeAmountOutD6: bigint;
  buyFeeD6: bigint;
  prizePoolFeeD6: bigint;
  totalQuoteDebitD6: bigint;
  postPoolQuoteReserveD6: bigint;
  postPoolOutcomeReserveD6: bigint;
  postOutstandingOutcomeSupplyD6: bigint;
}

export interface BondingCurveSellQuote {
  venue: 'bonding_curve';
  direction: 'sell';
  outcomeAmountInD6: bigint;
  grossQuoteOutputD6: bigint;
  sellBaseFeeD6: bigint;
  prizePoolFeeD6: bigint;
  sellPenaltyFeeD6: bigint;
  netQuoteOutputD6: bigint;
  penaltyApplied: boolean;
  postPoolQuoteReserveD6: bigint;
  postPoolOutcomeReserveD6: bigint;
  postOutstandingOutcomeSupplyD6: bigint;
}

export interface BondingCurveBuyInput {
  state: BondingCurveState;
  fees: BondingCurveFeeConfig;
  /** Quote that enters the curve. Buy fees are charged on top of this amount. */
  quoteAmountInD6: bigint;
}

export interface BondingCurveBuyForQuoteBudgetInput {
  state: BondingCurveState;
  fees: BondingCurveFeeConfig;
  /** Maximum total wallet debit, including fees charged on top of curve input. */
  maximumQuoteDebitD6: bigint;
}

export interface BondingCurveSellInput {
  state: BondingCurveState;
  fees: BondingCurveFeeConfig;
  outcomeAmountInD6: bigint;
  penaltyApplied: boolean;
}

export interface BondingCurveSellFeeExcludedInput {
  state: BondingCurveState;
  outcomeAmountInD6: bigint;
}

export interface BondingCurveSellForQuoteOutputInput {
  state: BondingCurveState;
  fees: BondingCurveFeeConfig;
  targetQuoteOutputD6: bigint;
  target: SellQuoteOutput;
  maximumOutcomeAmountInD6: bigint;
  penaltyApplied: boolean;
}

export interface BondingCurveSellForGrossQuoteOutputInput
  extends Omit<BondingCurveSellForQuoteOutputInput, 'target' | 'targetQuoteOutputD6'> {
  desiredGrossQuoteOutputD6: bigint;
}

export interface BondingCurvePostTradeStateInput {
  state: BondingCurveState;
  direction: 'buy' | 'sell';
  postPoolQuoteReserveD6: bigint;
  postPoolOutcomeReserveD6: bigint;
}

export function quoteBondingCurveBuyExactIn(
  input: BondingCurveBuyInput,
): PoolMathResult<BondingCurveBuyQuote> {
  const validationFailure = validateBondingCurveInput(input.state, input.fees);
  if (validationFailure !== null) return unavailable(validationFailure);
  if (!isPositiveU64(input.quoteAmountInD6)) return unavailable('invalid_amount');
  if (input.quoteAmountInD6 < input.fees.minimumQuoteTradeD6) {
    return unavailable('amount_below_minimum');
  }

  const postPoolQuoteReserveD6 = input.state.poolQuoteReserveD6 + input.quoteAmountInD6;
  if (!isU64(postPoolQuoteReserveD6)) return unavailable('invalid_reserves');

  const invariant = bondingCurveInvariant(input.state);
  const postPoolOutcomeReserveD6 = ceilDivide(invariant, postPoolQuoteReserveD6);
  if (postPoolOutcomeReserveD6 > input.state.poolOutcomeReserveD6) {
    return unavailable('invalid_reserves');
  }

  const outcomeAmountOutD6 = input.state.poolOutcomeReserveD6 - postPoolOutcomeReserveD6;
  if (outcomeAmountOutD6 === 0n) return unavailable('zero_progress');

  const buyFeeD6 = chargedBaseFee(input.quoteAmountInD6, input.fees.buyFeeBps, input.fees);
  const prizePoolFeeD6 = floorFee(
    input.quoteAmountInD6,
    input.fees.prizePoolFeeBps,
    BPS_DENOMINATOR,
  );
  const totalQuoteDebitD6 = input.quoteAmountInD6 + buyFeeD6 + prizePoolFeeD6;
  if (!isU64(totalQuoteDebitD6)) return unavailable('invalid_amount');

  return available({
    venue: 'bonding_curve',
    direction: 'buy',
    quoteToCurveD6: input.quoteAmountInD6,
    outcomeAmountOutD6,
    buyFeeD6,
    prizePoolFeeD6,
    totalQuoteDebitD6,
    postPoolQuoteReserveD6,
    postPoolOutcomeReserveD6,
    postOutstandingOutcomeSupplyD6: input.state.initialOutcomeSupplyD6 - postPoolOutcomeReserveD6,
  });
}

/** Finds the largest exact curve input whose fee-inclusive wallet debit fits the budget. */
export function quoteBondingCurveBuyForQuoteBudget(
  input: BondingCurveBuyForQuoteBudgetInput,
): PoolMathResult<BondingCurveBuyQuote> {
  const validationFailure = validateBondingCurveInput(input.state, input.fees);
  if (validationFailure !== null) return unavailable(validationFailure);
  if (!isPositiveU64(input.maximumQuoteDebitD6)) return unavailable('invalid_amount');

  const maximumReserveInputD6 = U64_MAX - input.state.poolQuoteReserveD6;
  let low = input.fees.minimumQuoteTradeD6;
  let high = minimum(input.maximumQuoteDebitD6, maximumReserveInputD6);
  if (low > high || bondingCurveBuyTotalDebitD6(low, input.fees) > input.maximumQuoteDebitD6) {
    return unavailable('amount_below_minimum');
  }

  while (low < high) {
    const candidate = (low + high + 1n) / 2n;
    if (bondingCurveBuyTotalDebitD6(candidate, input.fees) <= input.maximumQuoteDebitD6) {
      low = candidate;
    } else {
      high = candidate - 1n;
    }
  }

  return quoteBondingCurveBuyExactIn({
    state: input.state,
    fees: input.fees,
    quoteAmountInD6: low,
  });
}

export function quoteBondingCurveSellExactIn(
  input: BondingCurveSellInput,
): PoolMathResult<BondingCurveSellQuote> {
  const validationFailure = validateBondingCurveInput(input.state, input.fees);
  if (validationFailure !== null) return unavailable(validationFailure);
  const curve = quoteBondingCurveSellCurveExactIn(input);
  if (!curve.available) return curve;
  const { grossQuoteOutputD6, postPoolOutcomeReserveD6, postPoolQuoteReserveD6 } = curve.value;
  if (grossQuoteOutputD6 < input.fees.minimumQuoteTradeD6) {
    return unavailable('amount_below_minimum');
  }

  const sellBaseFeeD6 = chargedBaseFee(grossQuoteOutputD6, input.fees.sellBaseFeeBps, input.fees);
  const prizePoolFeeD6 = floorFee(grossQuoteOutputD6, input.fees.prizePoolFeeBps, BPS_DENOMINATOR);
  const sellPenaltyFeeD6 = input.penaltyApplied
    ? floorFee(grossQuoteOutputD6, input.fees.sellPenaltyFeeBps, BPS_DENOMINATOR)
    : 0n;
  const netQuoteOutputD6 = grossQuoteOutputD6 - sellBaseFeeD6 - prizePoolFeeD6 - sellPenaltyFeeD6;
  if (netQuoteOutputD6 <= 0n) return unavailable('zero_progress');

  return available({
    venue: 'bonding_curve',
    direction: 'sell',
    outcomeAmountInD6: input.outcomeAmountInD6,
    grossQuoteOutputD6,
    sellBaseFeeD6,
    prizePoolFeeD6,
    sellPenaltyFeeD6,
    netQuoteOutputD6,
    penaltyApplied: input.penaltyApplied,
    postPoolQuoteReserveD6,
    postPoolOutcomeReserveD6,
    postOutstandingOutcomeSupplyD6: input.state.initialOutcomeSupplyD6 - postPoolOutcomeReserveD6,
  });
}

/**
 * Exact reserve-only full-exit mark. It deliberately excludes fees and trade
 * eligibility; executable callers must use {@link quoteBondingCurveSellExactIn}.
 */
export function quoteBondingCurveSellFeeExcludedExactIn(
  input: BondingCurveSellFeeExcludedInput,
): PoolMathResult<bigint> {
  const quote = quoteBondingCurveSellCurveExactIn(input);
  return quote.available ? available(quote.value.grossQuoteOutputD6) : quote;
}

/**
 * Finds the smallest exact-input sell whose gross or net receipt reaches the
 * requested quote amount. This preserves every rounding step used by the
 * executable exact-input quote instead of algebraically approximating fees.
 */
export function quoteBondingCurveSellForQuoteOutput(
  input: BondingCurveSellForQuoteOutputInput,
): PoolMathResult<BondingCurveSellQuote> {
  const validationFailure = validateBondingCurveInput(input.state, input.fees);
  if (validationFailure !== null) return unavailable(validationFailure);

  const outstandingOutcomeSupplyD6 =
    input.state.initialOutcomeSupplyD6 - input.state.poolOutcomeReserveD6;
  if (input.maximumOutcomeAmountInD6 > outstandingOutcomeSupplyD6) {
    return unavailable('invalid_amount');
  }

  return solveMinimumInputForOutput({
    targetOutputD6: input.targetQuoteOutputD6,
    maximumInputD6: input.maximumOutcomeAmountInD6,
    quoteAtInput: (outcomeAmountInD6: bigint) => {
      const quote = quoteBondingCurveSellExactIn({
        state: input.state,
        fees: input.fees,
        outcomeAmountInD6,
        penaltyApplied: input.penaltyApplied,
      });
      if (!quote.available) return quote;
      return available({
        outputAmountD6: bondingCurveSellOutput(quote.value, input.target),
        value: quote.value,
      });
    },
  });
}

export function quoteBondingCurveSellForGrossQuoteOutput(
  input: BondingCurveSellForGrossQuoteOutputInput,
): PoolMathResult<BondingCurveSellQuote> {
  return quoteBondingCurveSellForQuoteOutput({
    state: input.state,
    fees: input.fees,
    targetQuoteOutputD6: input.desiredGrossQuoteOutputD6,
    target: 'gross',
    maximumOutcomeAmountInD6: input.maximumOutcomeAmountInD6,
    penaltyApplied: input.penaltyApplied,
  });
}

export function deriveBondingCurvePoolOutcomeReserveD6(
  initialOutcomeSupplyD6: bigint,
  outstandingOutcomeSupplyD6: bigint,
): PoolMathResult<bigint> {
  if (
    !isPositiveU64(initialOutcomeSupplyD6) ||
    !isU64(outstandingOutcomeSupplyD6) ||
    outstandingOutcomeSupplyD6 > initialOutcomeSupplyD6
  ) {
    return unavailable('invalid_reserves');
  }
  return available(initialOutcomeSupplyD6 - outstandingOutcomeSupplyD6);
}

export function deriveBondingCurveOutstandingOutcomeSupplyD6(
  initialOutcomeSupplyD6: bigint,
  poolOutcomeReserveD6: bigint,
): PoolMathResult<bigint> {
  if (
    !isPositiveU64(initialOutcomeSupplyD6) ||
    !isU64(poolOutcomeReserveD6) ||
    poolOutcomeReserveD6 > initialOutcomeSupplyD6
  ) {
    return unavailable('invalid_reserves');
  }
  return available(initialOutcomeSupplyD6 - poolOutcomeReserveD6);
}

/**
 * Validates one absolute post-trade reserve pair against the exact directional
 * contract rounding. BUY stores `T'=ceil(K/Q')`; SELL stores
 * `Q'=ceil(K/T')`, so the direction is load-bearing.
 */
export function checkedBondingCurvePostTradeState(
  input: BondingCurvePostTradeStateInput,
): PoolMathResult<BondingCurveState> {
  if (validateBondingCurveState(input.state) !== null) {
    return unavailable('invalid_reserves');
  }
  const postState: BondingCurveState = {
    virtualQuoteReserveD6: input.state.virtualQuoteReserveD6,
    poolQuoteReserveD6: input.postPoolQuoteReserveD6,
    initialOutcomeSupplyD6: input.state.initialOutcomeSupplyD6,
    poolOutcomeReserveD6: input.postPoolOutcomeReserveD6,
  };
  if (validateBondingCurveState(postState) !== null) {
    return unavailable('invalid_reserves');
  }
  const invariant = bondingCurveInvariant(input.state);
  const coherent =
    input.direction === 'buy'
      ? postState.poolQuoteReserveD6 > input.state.poolQuoteReserveD6 &&
        postState.poolOutcomeReserveD6 < input.state.poolOutcomeReserveD6 &&
        postState.poolOutcomeReserveD6 === ceilDivide(invariant, postState.poolQuoteReserveD6)
      : postState.poolQuoteReserveD6 < input.state.poolQuoteReserveD6 &&
        postState.poolOutcomeReserveD6 > input.state.poolOutcomeReserveD6 &&
        postState.poolQuoteReserveD6 === ceilDivide(invariant, postState.poolOutcomeReserveD6);
  return coherent ? available(postState) : unavailable('invalid_reserves');
}

export function bondingCurveRealQuoteReserveD6(state: BondingCurveState): PoolMathResult<bigint> {
  if (!isPositiveU64(state.virtualQuoteReserveD6) || !isPositiveU64(state.poolQuoteReserveD6)) {
    return unavailable('invalid_reserves');
  }
  if (state.poolQuoteReserveD6 < state.virtualQuoteReserveD6) {
    return unavailable('invalid_reserves');
  }
  return available(state.poolQuoteReserveD6 - state.virtualQuoteReserveD6);
}

export function bondingCurveSpotPriceD9(state: BondingCurveState): PoolMathResult<bigint> {
  const validationFailure = validateBondingCurveState(state);
  if (validationFailure !== null) return unavailable(validationFailure);
  return reserveRatioSpotPriceD9(state.poolQuoteReserveD6, state.poolOutcomeReserveD6);
}

function bondingCurveInvariant(state: BondingCurveState): bigint {
  return state.virtualQuoteReserveD6 * state.initialOutcomeSupplyD6;
}

interface BondingCurveSellCurveQuote {
  grossQuoteOutputD6: bigint;
  postPoolQuoteReserveD6: bigint;
  postPoolOutcomeReserveD6: bigint;
}

function quoteBondingCurveSellCurveExactIn(
  input: BondingCurveSellFeeExcludedInput,
): PoolMathResult<BondingCurveSellCurveQuote> {
  const validationFailure = validateBondingCurveState(input.state);
  if (validationFailure !== null) return unavailable(validationFailure);
  if (!isPositiveU64(input.outcomeAmountInD6)) return unavailable('invalid_amount');

  const outstandingOutcomeSupplyD6 =
    input.state.initialOutcomeSupplyD6 - input.state.poolOutcomeReserveD6;
  if (input.outcomeAmountInD6 > outstandingOutcomeSupplyD6) {
    return unavailable('invalid_amount');
  }

  const postPoolOutcomeReserveD6 = input.state.poolOutcomeReserveD6 + input.outcomeAmountInD6;
  const invariant = bondingCurveInvariant(input.state);
  const postPoolQuoteReserveD6 = ceilDivide(invariant, postPoolOutcomeReserveD6);
  if (postPoolQuoteReserveD6 > input.state.poolQuoteReserveD6) {
    return unavailable('invalid_reserves');
  }

  const grossQuoteOutputD6 = input.state.poolQuoteReserveD6 - postPoolQuoteReserveD6;
  if (grossQuoteOutputD6 === 0n) return unavailable('zero_progress');
  return available({
    grossQuoteOutputD6,
    postPoolQuoteReserveD6,
    postPoolOutcomeReserveD6,
  });
}

function bondingCurveBuyTotalDebitD6(quoteAmountInD6: bigint, fees: BondingCurveFeeConfig): bigint {
  return (
    quoteAmountInD6 +
    chargedBaseFee(quoteAmountInD6, fees.buyFeeBps, fees) +
    floorFee(quoteAmountInD6, fees.prizePoolFeeBps, BPS_DENOMINATOR)
  );
}

function bondingCurveSellOutput(quote: BondingCurveSellQuote, target: SellQuoteOutput): bigint {
  return target === 'gross' ? quote.grossQuoteOutputD6 : quote.netQuoteOutputD6;
}

function minimum(left: bigint, right: bigint): bigint {
  return left < right ? left : right;
}

function validateBondingCurveInput(
  state: BondingCurveState,
  fees: BondingCurveFeeConfig,
): PoolMathUnavailableReason | null {
  const stateFailure = validateBondingCurveState(state);
  if (stateFailure !== null) return stateFailure;
  if (
    !isRate(fees.backerFeeOmissionBps ?? 0n, BPS_DENOMINATOR) ||
    !isRate(fees.buyFeeBps, BPS_DENOMINATOR) ||
    !isRate(fees.sellBaseFeeBps, BPS_DENOMINATOR) ||
    !isRate(fees.sellPenaltyFeeBps, BPS_DENOMINATOR) ||
    !isRate(fees.prizePoolFeeBps, BPS_DENOMINATOR) ||
    !isPositiveU64(fees.minimumQuoteTradeD6) ||
    fees.sellBaseFeeBps + fees.sellPenaltyFeeBps + fees.prizePoolFeeBps > BPS_DENOMINATOR
  ) {
    return 'invalid_fee_config';
  }
  return null;
}

function validateBondingCurveState(state: BondingCurveState): PoolMathUnavailableReason | null {
  if (
    !isPositiveU64(state.virtualQuoteReserveD6) ||
    !isPositiveU64(state.poolQuoteReserveD6) ||
    !isPositiveU64(state.initialOutcomeSupplyD6) ||
    !isPositiveU64(state.poolOutcomeReserveD6) ||
    state.poolQuoteReserveD6 < state.virtualQuoteReserveD6 ||
    state.poolOutcomeReserveD6 > state.initialOutcomeSupplyD6
  ) {
    return 'invalid_reserves';
  }

  const invariant = bondingCurveInvariant(state);
  // BUY stores T'=ceil(K/Q'); SELL stores Q'=ceil(K/T'). Because ceil is not
  // invertible, a valid post-SELL state need not satisfy T'=ceil(K/Q'). Accept
  // either exact directional envelope while rejecting incoherent reserve pairs.
  const isBuyOriginState =
    state.poolOutcomeReserveD6 === ceilDivide(invariant, state.poolQuoteReserveD6);
  const isSellOriginState =
    state.poolQuoteReserveD6 === ceilDivide(invariant, state.poolOutcomeReserveD6);
  if (!isBuyOriginState && !isSellOriginState) return 'invalid_reserves';
  return null;
}

function chargedBaseFee(amountD6: bigint, baseBps: bigint, fees: BondingCurveFeeConfig): bigint {
  const baseFeeD6 = floorFee(amountD6, baseBps, BPS_DENOMINATOR);
  return baseFeeD6 - floorFee(baseFeeD6, fees.backerFeeOmissionBps ?? 0n, BPS_DENOMINATOR);
}
